import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress,
} from "@mui/material";
import Controls from "../../../../atoms/Controls";
import moment, { Moment } from "moment";
import { WeeklyWorkDayDto } from "../../../../api-client";
import { formatDateDisplayOrNull } from "../../../../helpers/dateFormat";

/** Pending change to be applied after version confirmation */
export interface PendingChange {
  type: "create" | "update";
  weeklyWorkDay: WeeklyWorkDayDto;
}

interface EffectiveDateDialogProps {
  open: boolean;
  onClose: () => void;
  /** @deprecated No longer used - banner handles continue editing flow */
  onContinueEditing?: () => void;
  onConfirm: (effectiveDate: string) => void;
  workPlanId?: number;
  /** The original validity start of the plan being versioned */
  originalValidityStart?: string;
  /** Pending changes count for message display */
  pendingChanges?: PendingChange[];
  /** Loading state - disables buttons and shows spinner */
  loading?: boolean;
  /** Mode: 'version' for versioning, 'duplicate' for duplicating */
  mode?: "version" | "duplicate";
  /**
   * Why the last attempt was refused, straight from the API — e.g. the overlap
   * rule "Pro Standort darf pro Tag nur ein Arbeitsplan gelten."
   *
   * Shown here rather than left to the snackbar alone: a rejected attempt keeps
   * this dialog open, so the reason has to be readable inside it. Otherwise the
   * user sees an unchanged dialog and a toast behind the modal (MD-351).
   */
  errorMessage?: string | null;
}

/**
 * Dialog shown when applying pending work plan changes.
 * Lets user pick the effective date for the new version.
 */
const EffectiveDateDialog: React.FC<EffectiveDateDialogProps> = ({
  open,
  onClose,
  onContinueEditing,
  onConfirm,
  workPlanId,
  originalValidityStart,
  pendingChanges = [],
  loading = false,
  mode = "version",
  errorMessage = null,
}) => {
  const [effectiveDate, setEffectiveDate] = useState<Moment | null>(moment());

  // Reset to today when dialog opens
  useEffect(() => {
    if (open) {
      setEffectiveDate(moment());
    }
  }, [open]);

  const handleDateChange = (date: Date | { target: { value: string | null } } | null) => {
    if (date instanceof Date) {
      setEffectiveDate(moment(date));
    } else if (date?.target?.value) {
      setEffectiveDate(moment(date.target.value));
    } else {
      setEffectiveDate(null);
    }
  };

  const handleConfirm = () => {
    if (effectiveDate?.isValid()) {
      onConfirm(effectiveDate.format("YYYY-MM-DD"));
    }
  };

  const isValid = mode === "duplicate" 
    ? effectiveDate?.isValid() 
    : effectiveDate?.isValid() && effectiveDate.isSameOrAfter(moment(), "day");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "duplicate" ? "Arbeitsplan duplizieren" : "Änderungen anwenden"}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {mode === "duplicate" ? (
            <>Es wird ein neuer Arbeitsplan mit allen Arbeitszeiten des ausgewählten Plans erstellt. Der ursprüngliche Plan bleibt unverändert.</>
          ) : (
            <>Der Arbeitsplan hat bereits historische Daten (gültig seit{" "}
            {/* The API sends a UTC ISO string; shown raw it read
                "2026-08-31T22:00:00.000Z" for a plan starting 01.09. */}
            {formatDateDisplayOrNull(originalValidityStart) ?? "Vergangenheit"}).
            <br />
            <br />
            Um die Historie zu erhalten, wird eine <strong>neue Version</strong> des Arbeitsplans
            erstellt.</>
          )}
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          {mode === "duplicate" 
            ? "Ab welchem Datum soll der neue Arbeitsplan gelten?"
            : `Ab welchem Datum sollen die ${pendingChanges.length === 1 ? "Änderung" : `${pendingChanges.length} Änderungen`} gelten?`}
        </DialogContentText>

        <Controls.DatePicker
          label="Gültig ab"
          value={effectiveDate?.format("YYYY-MM-DD")}
          onChange={handleDateChange}
          minDate={mode === "version" ? moment().format("YYYY-MM-DD") : undefined}
        />

        {mode === "version" && effectiveDate && !isValid && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Das Datum muss heute oder in der Zukunft liegen.
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? "Wird erstellt..." : mode === "duplicate" ? "Duplizieren" : "Neue Version erstellen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EffectiveDateDialog;
